import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Calendar as CalendarIcon, Clock, Video, CalendarPlus, CreditCard, CheckCircle2,
  Hourglass, Sparkles, Copy
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
};

// Picks the single appointment that represents the patient's "current journey"
function pickActiveAppointment(appointments) {
  if (!appointments || appointments.length === 0) return null;
  const order = { scheduled: 1, awaiting_payment: 2, pending_approval: 3 };
  const active = appointments.filter(a => order[a.status]);
  if (active.length === 0) return null;
  active.sort((a, b) => {
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time);
  });
  return active[0];
}

export const NextActionHero = ({ patientData, appointments, onRefresh }) => {
  const navigate = useNavigate();
  const apt = pickActiveAppointment(appointments);

  // Booking dialog state
  const [bookOpen, setBookOpen] = useState(false);
  const [bookDate, setBookDate] = useState('');
  const [bookTime, setBookTime] = useState('');
  const [bookType, setBookType] = useState(patientData?.consultation_type || 'clinica_geral');
  const [bookNotes, setBookNotes] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookLoading, setBookLoading] = useState(false);

  // Pix payment dialog state
  const [pixData, setPixData] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Next scheduled return (doctor's suggestion)
  const [nextReturn, setNextReturn] = useState(null);

  const fetchNextReturn = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patient/my-next-return`, { withCredentials: true });
      setNextReturn(data && data.exists ? data : null);
    } catch (e) { setNextReturn(null); }
  }, []);

  useEffect(() => { fetchNextReturn(); }, [fetchNextReturn, appointments]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/doctor/availability`, { withCredentials: true });
      setAvailableDates((data || []).filter(d => d.date >= todayStr() && d.slots?.length > 0));
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (bookOpen) fetchAvailableDates();
  }, [bookOpen, fetchAvailableDates]);

  // Auto-fetch slots whenever bookDate is set while dialog is open
  useEffect(() => {
    if (!bookOpen || !bookDate) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/doctor/availability?date=${bookDate}`, { withCredentials: true });
        if (!cancelled) setAvailableSlots(data?.available_slots || []);
      } catch (e) { if (!cancelled) setAvailableSlots([]); }
    })();
    return () => { cancelled = true; };
  }, [bookOpen, bookDate]);

  useEffect(() => {
    if (patientData?.consultation_type) setBookType(patientData.consultation_type);
  }, [patientData]);

  const handleSelectDate = async (date) => {
    setBookDate(date); setBookTime('');
    try {
      const { data } = await axios.get(`${API_URL}/api/doctor/availability?date=${date}`, { withCredentials: true });
      setAvailableSlots(data?.available_slots || []);
    } catch (e) { setAvailableSlots([]); }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    setBookLoading(true);
    try {
      await axios.post(`${API_URL}/api/patient/appointments/book`, {
        date: bookDate, time: bookTime, type: bookType, notes: bookNotes
      }, { withCredentials: true });
      toast.success('Solicitação enviada! Aguarde a confirmação da Dra. Fernanda.');
      setBookOpen(false); setBookDate(''); setBookTime(''); setBookNotes('');
      setNextReturn(null); // backend clears it too; keep UI in sync immediately
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar consulta');
    }
    setBookLoading(false);
  };

  const handleOpenPix = async () => {
    setPixLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/pix/generate/${apt.appointment_id}`, { withCredentials: true });
      setPixData({ ...data, appointmentId: apt.appointment_id });
    } catch (error) {
      toast.error('Erro ao gerar PIX. Tente novamente.');
    }
    setPixLoading(false);
  };

  const handleConfirmPayment = async () => {
    setConfirmLoading(true);
    try {
      await axios.post(`${API_URL}/api/pix/confirm/${pixData.appointmentId}`, {}, { withCredentials: true });
      toast.success('Pagamento confirmado! Consulta agendada.');
      setPixData(null);
      onRefresh && onRefresh();
    } catch (error) {
      toast.error('Erro ao confirmar pagamento');
    }
    setConfirmLoading(false);
  };

  const copyPix = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Chave PIX copiada!');
  };

  // ===================== HERO CONTENT BY STATE =====================

  const HeroShell = ({ accent = '#0F1B3D', icon: Icon, eyebrow, title, children, testid }) => (
    <div
      className="relative overflow-hidden rounded-2xl border-2 p-6 sm:p-10 shadow-sm"
      style={{ borderColor: `${accent}30`, background: `linear-gradient(135deg, ${accent}08 0%, #FFFFFF 60%)` }}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        {Icon && (
          <div className="p-3 rounded-xl shrink-0" style={{ background: `${accent}15` }}>
            <Icon className="h-6 w-6" style={{ color: accent }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>{eyebrow}</p>
          <h2 className="text-2xl sm:text-3xl font-medium text-[#0F1B3D] mt-1 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h2>
          {children}
        </div>
      </div>
    </div>
  );

  // STATE 1: No active appointment
  if (!apt) {
    // STATE 1a: If the doctor set a "Próximo Retorno Programado", show that card
    // instead of the generic empty message. Clicking Solicitar Agendamento opens
    // the booking dialog pre-populated with the suggested date/time.
    if (nextReturn) {
      const openBookingWithSuggested = () => {
        setBookDate(nextReturn.date || '');
        setBookTime(nextReturn.time || '');
        if (nextReturn.notes) setBookNotes(nextReturn.notes);
        setBookOpen(true);
      };
      return (
        <>
          <HeroShell
            accent="#A8174E"
            icon={CalendarIcon}
            eyebrow="Recomendação da Dra. Fernanda"
            title="Próximo retorno sugerido"
            testid="hero-next-return"
          >
            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-[#E5DCC8] mb-4">
              <CalendarIcon className="h-5 w-5 text-[#A8174E]" />
              <div className="text-sm">
                <p className="font-semibold text-[#0F1B3D] capitalize" data-testid="next-return-date-display">
                  {nextReturn.date ? fmtDate(nextReturn.date) : 'Data a definir'}
                </p>
                {nextReturn.time && (
                  <p className="text-[#5F6B85]" data-testid="next-return-time-display">às {nextReturn.time}</p>
                )}
              </div>
            </div>
            {nextReturn.notes && (
              <p className="text-sm text-[#5F6B85] mb-4 max-w-lg" data-testid="next-return-notes-display">
                <strong className="text-[#0F1B3D]">Observação: </strong>{nextReturn.notes}
              </p>
            )}
            <p className="text-[#5F6B85] mb-6 max-w-lg text-sm">
              Esta é apenas uma recomendação — o horário só será reservado depois que você confirmar.
            </p>
            <Button
              size="lg"
              className="bg-[#A8174E] hover:bg-[#8E1142] text-white px-8 h-12 text-base"
              onClick={openBookingWithSuggested}
              data-testid="next-return-request-btn"
            >
              <CalendarPlus className="h-5 w-5 mr-2" />
              Solicitar Agendamento
            </Button>
          </HeroShell>
          <BookingDialog />
        </>
      );
    }

    // STATE 1b: No active appointment and no scheduled return — original empty state
    return (
      <>
        <HeroShell
          accent="#0F1B3D"
          icon={Sparkles}
          eyebrow="Próximo Passo"
          title="Você ainda não possui consulta agendada"
          testid="hero-no-appointment"
        >
          <p className="text-[#5F6B85] mb-6 max-w-lg">
            Agende sua primeira consulta com a Dra. Fernanda em poucos cliques. Você escolhe a data e o horário.
          </p>
          <Button
            size="lg"
            className="bg-[#A8174E] hover:bg-[#8E1142] text-white px-8 h-12 text-base"
            onClick={() => setBookOpen(true)}
            data-testid="hero-book-btn"
          >
            <CalendarPlus className="h-5 w-5 mr-2" />
            Agendar Consulta
          </Button>
        </HeroShell>
        <BookingDialog />
      </>
    );
  }

  // STATE 2: pending_approval
  if (apt.status === 'pending_approval') {
    return (
      <HeroShell
        accent="#B8845C"
        icon={Hourglass}
        eyebrow="Aguardando Confirmação"
        title="Estamos confirmando seu horário"
        testid="hero-pending"
      >
        <p className="text-[#5F6B85] mb-4">
          Sua solicitação foi enviada para a <strong className="text-[#0F1B3D]">Dra. Fernanda</strong>.
          Você será notificado(a) assim que ela confirmar.
        </p>
        <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-[#B8845C]/30">
          <CalendarIcon className="h-5 w-5 text-[#B8845C]" />
          <div className="text-sm">
            <p className="font-semibold text-[#0F1B3D] capitalize">{fmtDate(apt.date)}</p>
            <p className="text-[#5F6B85]">às {apt.time}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-[#7A85A0]">
          <Hourglass className="h-3.5 w-3.5 animate-pulse" />
          Aguardando confirmação da Dra. Fernanda
        </div>
      </HeroShell>
    );
  }

  // STATE 3: awaiting_payment (confirmed, needs to pay)
  if (apt.status === 'awaiting_payment') {
    return (
      <>
        <HeroShell
          accent="#A8174E"
          icon={CreditCard}
          eyebrow="Consulta Confirmada"
          title="Realize o pagamento para garantir sua consulta"
          testid="hero-awaiting-payment"
        >
          <p className="text-[#5F6B85] mb-4">
            A Dra. Fernanda confirmou seu horário. Para concluir, faça o pagamento via PIX.
          </p>
          <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-[#A8174E]/30 mb-5">
            <CalendarIcon className="h-5 w-5 text-[#A8174E]" />
            <div className="text-sm">
              <p className="font-semibold text-[#0F1B3D] capitalize">{fmtDate(apt.date)}</p>
              <p className="text-[#5F6B85]">às {apt.time}</p>
            </div>
          </div>
          <div>
            <Button
              size="lg"
              className="bg-[#A8174E] hover:bg-[#8E1142] text-white px-8 h-12 text-base"
              onClick={handleOpenPix}
              disabled={pixLoading}
              data-testid="hero-pay-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {pixLoading ? 'Gerando PIX...' : 'Realizar Pagamento'}
            </Button>
          </div>
        </HeroShell>
        <PixDialog />
      </>
    );
  }

  // STATE 4: scheduled (paid, ready)
  if (apt.status === 'scheduled') {
    const meetLink = apt.meet_link;
    return (
      <HeroShell
        accent="#0F1B3D"
        icon={CheckCircle2}
        eyebrow="Tudo Pronto"
        title="Sua consulta está confirmada"
        testid="hero-scheduled"
      >
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="flex items-baseline gap-3 mb-2">
              <div>
                <p className="text-xs text-[#7A85A0] uppercase tracking-wider mb-1">Quando</p>
                <p className="text-2xl font-semibold text-[#0F1B3D] capitalize">{fmtDate(apt.date)}</p>
                <p className="text-lg text-[#5F6B85]">às {apt.time}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <img src="/images/dra-fernanda-photo.png" alt="Dra. Fernanda" className="w-8 h-8 rounded-full object-cover object-top" />
                <span className="text-[#5F6B85]">Dra. Fernanda Garcia Passos</span>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#0F1B3D]/10 text-[#0F1B3D] text-xs font-medium">
                <CheckCircle2 className="h-3 w-3" /> Pagamento confirmado
              </span>
            </div>
          </div>
          <div className="shrink-0">
            {meetLink ? (
              <Button
                size="lg"
                className="bg-[#A8174E] hover:bg-[#8E1142] text-white px-8 h-12 text-base"
                onClick={() => window.open(meetLink, '_blank', 'noopener,noreferrer')}
                data-testid="hero-join-meet-btn"
              >
                <Video className="h-5 w-5 mr-2" />
                Entrar na Consulta
              </Button>
            ) : (
              <div className="text-xs text-[#7A85A0] max-w-[180px] text-right">
                O link do Google Meet estará disponível em breve.
              </div>
            )}
          </div>
        </div>
      </HeroShell>
    );
  }

  // FALLBACK
  return null;

  // ===================== Dialogs (defined inside to share state) =====================

  function BookingDialog() {
    return (
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-md" data-testid="book-dialog">
          <DialogHeader><DialogTitle>Agendar Consulta</DialogTitle></DialogHeader>
          <form onSubmit={handleBook} className="space-y-4">
            <div className="space-y-2">
              <Label>Escolha uma data</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {availableDates.length === 0 && (
                  <p className="text-xs text-[#7A85A0]">Nenhuma data disponível no momento</p>
                )}
                {availableDates.slice(0, 12).map(av => (
                  <button key={av.date} type="button" onClick={() => handleSelectDate(av.date)}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${bookDate === av.date ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]' : 'border-[#E5DCC8] text-[#5F6B85] hover:border-[#0F1B3D]'}`}
                    data-testid={`book-date-${av.date}`}>
                    {new Date(av.date + 'T12:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  </button>
                ))}
              </div>
            </div>
            {bookDate && (
              <div className="space-y-2">
                <Label>Horário</Label>
                <div className="flex flex-wrap gap-2">
                  {availableSlots.length === 0 && <p className="text-xs text-[#7A85A0]">Sem horários livres nesta data</p>}
                  {availableSlots.map(slot => (
                    <button key={slot} type="button" onClick={() => setBookTime(slot)}
                      className={`px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1 ${bookTime === slot ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]' : 'border-[#E5DCC8] text-[#5F6B85] hover:border-[#0F1B3D]'}`}
                      data-testid={`book-time-${slot}`}>
                      <Clock className="h-3 w-3" />{slot}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Input value={bookNotes} onChange={e => setBookNotes(e.target.value)} placeholder="Algum sintoma ou contexto" />
            </div>
            <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" disabled={!bookDate || !bookTime || bookLoading} data-testid="book-submit-btn">
              {bookLoading ? 'Enviando...' : 'Solicitar Consulta'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  function PixDialog() {
    return (
      <Dialog open={!!pixData} onOpenChange={(open) => { if (!open) setPixData(null); }}>
        <DialogContent className="max-w-sm" data-testid="pix-dialog">
          <DialogHeader><DialogTitle>Pagamento via PIX</DialogTitle></DialogHeader>
          {pixData && (
            <div className="space-y-4 text-center">
              <div className="bg-[#FAF6EE] rounded-xl p-4">
                <p className="text-sm text-[#5F6B85] mb-1">Valor da Consulta</p>
                <p className="text-3xl font-bold text-[#0F1B3D]">R$ {pixData.amount?.toFixed(2)}</p>
              </div>
              <div>
                <img src={pixData.qrcode_base64} alt="QR Code PIX" className="mx-auto w-48 h-48 rounded-lg" data-testid="pix-qrcode" />
              </div>
              <div className="bg-[#F5F0E8] rounded-lg p-3">
                <p className="text-xs text-[#7A85A0] mb-1">Chave PIX (Email)</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-[#0F1B3D] flex-1 break-all">{pixData.pix_key}</code>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => copyPix(pixData.pix_key)} data-testid="copy-pix-key">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-[#F5F0E8] rounded-lg p-3">
                <p className="text-xs text-[#7A85A0] mb-1">Copia e Cola (Payload)</p>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] text-[#5F6B85] flex-1 break-all max-h-16 overflow-hidden">{pixData.payload?.slice(0, 80)}...</code>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => copyPix(pixData.payload)} data-testid="copy-pix-payload">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-[#7A85A0]">Titular: {pixData.pix_name}</p>
              <Button
                className="w-full bg-[#0F1B3D] hover:bg-[#0F1B3D]/80 text-white"
                onClick={handleConfirmPayment}
                disabled={confirmLoading}
                data-testid="confirm-payment-btn"
              >
                {confirmLoading ? 'Confirmando...' : 'Já fiz o pagamento'}
              </Button>
              <p className="text-[10px] text-[#7A85A0]">Após o pagamento, clique no botão acima para liberar o link da videochamada</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
};
